"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import Home from "@public/svgs/home.svg";
import RightArrow from "@public/svgs/right_arrow.svg";
import { BOARD_CATEGORIES } from "@/constants/board";
import type { BoardCategory } from "@/types/board";

/**
 * PageBreadcrumbs 컴포넌트 Props 인터페이스
 *
 * 페이지 브레드크럼에 표시할 현재 카테고리 정보를 정의합니다.
 */
interface PageBreadcrumbsProps {
  category: BoardCategory;
  /** margin-bottom 제거 여부 */
  noMargin?: boolean;
}

/**
 * 페이지 브레드크럼 컴포넌트
 *
 * 페이지 상단에 표시되는 네비게이션 경로를 보여주는 컴포넌트입니다.
 * 계층적 메뉴 구조를 지원하며, 드롭다운을 통해 다른 카테고리로 쉽게 이동할 수 있습니다.
 *
 * 주요 기능:
 * - 홈으로 돌아가기 링크
 * - 메인 메뉴 드롭다운 (RISE사업단, 게시판, 자료실 등)
 * - 서브 메뉴 드롭다운 (각 메인 메뉴별 세부 카테고리)
 * - 현재 위치 하이라이트
 * - 외부 클릭 시 드롭다운 자동 닫기
 * - 반응형 디자인
 */
export function PageBreadcrumbs({
  category,
  noMargin = false,
}: PageBreadcrumbsProps) {
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false);
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const mainMenuRef = useRef<HTMLDivElement>(null);
  const subMenuRef = useRef<HTMLDivElement>(null);

  // 컴포넌트 마운트 및 외부 클릭 이벤트 처리
  useEffect(() => {
    setIsMounted(true);

    const handleClickOutside = (event: MouseEvent) => {
      if (
        mainMenuRef.current &&
        !mainMenuRef.current.contains(event.target as Node)
      ) {
        setIsMainMenuOpen(false);
      }
      if (
        subMenuRef.current &&
        !subMenuRef.current.contains(event.target as Node)
      ) {
        setIsSubMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /**
   * 현재 카테고리의 상위 카테고리를 반환하는 함수
   *
   * @param currentCategory - 현재 카테고리
   * @returns 상위 카테고리 라벨
   */
  const getParentCategory = (currentCategory: BoardCategory) => {
    return currentCategory.mainMenu;
  };

  /**
   * 메인 메뉴 카테고리 목록을 반환하는 함수
   *
   * @returns 메인 메뉴 카테고리 배열
   */
  const getMainMenuCategories = () => {
    return [
      { id: "rise", label: "RISE사업단", href: "/greeting" },
      { id: "rise-business", label: "RISE추진사업", href: "/rise-business" },
      { id: "board", label: "알림마당", href: "/notices" },
      { id: "newsletter", label: "홍보", href: "/newsletters" },
      { id: "resources", label: "자료실", href: "/resources" },
    ];
  };

  /**
   * 상위 카테고리에 속하는 서브 카테고리 목록을 반환하는 함수
   *
   * @param parentCategory - 상위 카테고리 라벨
   * @returns 서브 카테고리 배열
   */
  const getSubCategories = (parentCategory: string) => {
    if (parentCategory === "RISE사업단") {
      return BOARD_CATEGORIES.filter((cat) =>
        ["greeting", "organization", "living-research", "location"].includes(cat.id)
      );
    }
    if (parentCategory === "RISE추진사업") {
      return BOARD_CATEGORIES.filter((cat) => cat.id === "rise-business");
    }
    if (parentCategory === "사업공고") {
      return BOARD_CATEGORIES.filter((cat) => cat.id === "rise-projects");
    }
    if (parentCategory === "알림마당") {
      const orderedIds = ["notices", "recruit", "rise-projects"];
      return orderedIds
        .map((id) => BOARD_CATEGORIES.find((cat) => cat.id === id))
        .filter((cat): cat is BoardCategory => Boolean(cat));
    }
    if (parentCategory === "자료실") {
      return BOARD_CATEGORIES.filter((cat) => cat.id === "resources");
    }
    if (parentCategory === "홍보") {
      return BOARD_CATEGORIES.filter((cat) => cat.id === "newsletter");
    }
    return [];
  };

  const parentCategory = getParentCategory(category);
  const mainMenuCategories = getMainMenuCategories();
  const subCategories = getSubCategories(parentCategory);
  const hasMultipleSubCategories = subCategories.length > 1;

  // 서버와 클라이언트 초기 상태를 일치시키기 위해 isMounted 체크
  const shouldShowMainMenu = isMounted && isMainMenuOpen;
  const shouldShowSubMenu = isMounted && isSubMenuOpen;

  return (
    <nav
      className={`h-12 sm:h-14 border-b border-border ${
        noMargin ? "" : "mb-16 sm:mb-20 lg:mb-24"
      }`}
    >
      <div className="w-full max-w-[1540px] flex items-center mx-auto px-4 sm:px-5 h-full">
        {/* 홈 링크 */}
        <Link
          href="/"
          className="h-12 w-12 sm:h-14 sm:w-14 bg-primary flex items-center justify-center"
        >
          <Home className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
        </Link>

        {/* 첫 번째 화살표 구분자 */}
        <div className="px-4 sm:px-6 lg:px-8">
          <RightArrow
            width={8}
            height={16}
            className="text-border sm:w-2.5 sm:h-5"
          />
        </div>

        {/* 메인 메뉴 드롭다운 */}
        <div className="relative" ref={mainMenuRef}>
          <button
            onClick={() => {
              setIsMainMenuOpen(!isMainMenuOpen);
              if (hasMultipleSubCategories) {
                setIsSubMenuOpen(false);
              }
            }}
            className="flex items-center text-base sm:text-lg hover:text-primary transition-colors"
          >
            {parentCategory}
            {/* 드롭다운 화살표 */}
            <svg
              className={`ml-2 sm:ml-3 h-3 w-3 sm:h-4 sm:w-4 ${
                shouldShowMainMenu ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* 메인 메뉴 드롭다운 목록 */}
          {shouldShowMainMenu && (
            <div className="absolute left-1/2 -translate-x-1/2 top-full z-50 py-2 mt-3 sm:mt-5 min-w-32 sm:min-w-40 border border-border rounded-lg sm:rounded-xl overflow-hidden bg-background shadow-lg">
              {mainMenuCategories.map((mainCat) => (
                <Link
                  key={mainCat.id}
                  href={mainCat.href}
                  className={`block w-full px-3 sm:px-4 py-2 text-left text-sm sm:text-base hover:bg-gray-50 ${
                    mainCat.label === parentCategory
                      ? "font-extrabold text-primary"
                      : ""
                  }`}
                  onClick={() => setIsMainMenuOpen(false)}
                >
                  {mainCat.label}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* 서브 메뉴가 있는 경우에만 표시 */}
        {hasMultipleSubCategories && (
          <>
            {/* 두 번째 화살표 구분자 */}
            <div className="px-4 sm:px-6 lg:px-8">
              <RightArrow
                width={8}
                height={16}
                className="text-border sm:w-2.5 sm:h-5"
              />
            </div>

            {/* 서브 메뉴 드롭다운 */}
            <div className="relative" ref={subMenuRef}>
              <button
                onClick={() => {
                  setIsSubMenuOpen(!isSubMenuOpen);
                  setIsMainMenuOpen(false);
                }}
                className="flex items-center text-base sm:text-lg hover:text-primary transition-colors"
              >
                {category.label}
                {/* 드롭다운 화살표 */}
                <svg
                  className={`ml-2 sm:ml-3 h-3 w-3 sm:h-4 sm:w-4 transition-transform duration-100 ${
                    shouldShowSubMenu ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* 서브 메뉴 드롭다운 목록 */}
              {shouldShowSubMenu && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full z-50 py-2 mt-3 sm:mt-5 min-w-32 sm:min-w-40 border border-border rounded-lg sm:rounded-xl overflow-hidden bg-background shadow-lg">
                  {subCategories.map((subCat) => (
                    <Link
                      key={subCat.id}
                      href={subCat.href}
                      className={`block w-full px-3 sm:px-4 py-2 text-left text-sm sm:text-base hover:bg-gray-50 ${
                        subCat.id === category.id
                          ? "font-extrabold text-primary"
                          : ""
                      }`}
                      onClick={() => setIsSubMenuOpen(false)}
                    >
                      {subCat.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </nav>
  );
}
